import type { FC } from "react";

import classNames from "classnames";
import dompurify from "isomorphic-dompurify";

import { useConfig } from "@jti/app-context/useConfig";
import { QuantityInput } from "@jti/core-components/components/QuantityInput";
import type { AddressStored } from "@jti/fe-common";
import type { RewardType } from "@jti/rest-api-types";
import { useFormContext } from "@jti/ui/components/Form";
import { useTranslations } from "@jti/ui/providers/useTranslations";
import { formatDate } from "@jti/ui/utils/formatDate";

import type { RedeemDialogV2Props } from "../../RedeemDialogV2.types";
import { ActionButtons } from "../ActionButtons";
import { AddressDeliveryDetails } from "../AddressDeliveryDetails";
import { Line } from "../Line";

import styles from "./PrizeDetails.module.scss";

const { sanitize } = dompurify;

export type PrizeDetailsProps = Pick<
  RedeemDialogV2Props,
  "onCloseDialog" | "pointsBalance" | "prizeData" | "quantityAriaLabel" | "increaseAriaLabel" | "decreaseAriaLabel"
> & {
  prizeQuantity: number;
  emailAddress?: string;
  onConfirm: () => void;
  onEditAddress: () => void;
  onSetPrizeQuantity: (value: number) => void;
  userAddress?: AddressStored;
  isConfirmButtonLoading?: boolean;
  isAddressBlockVisible?: boolean;
  isLotteryAddressRequirementDisabled?: boolean;
  rewardType: RewardType;
};

export const PrizeDetails: FC<PrizeDetailsProps> = ({
  prizeData,
  rewardType,
  userAddress,
  emailAddress = "",
  prizeQuantity,
  pointsBalance,
  isAddressBlockVisible,
  isLotteryAddressRequirementDisabled = false,
  isConfirmButtonLoading = false,
  onConfirm,
  onCloseDialog,
  onEditAddress,
  onSetPrizeQuantity,
  quantityAriaLabel,
  increaseAriaLabel,
  decreaseAriaLabel,
}) => {
  const { __ } = useTranslations();
  const { TERMS_PAGE_URL } = useConfig();
  const {
    formState: { isValid },
  } = useFormContext();

  const { time_to, points, name: prizeName } = prizeData;
  const calculatedPoints = points * prizeQuantity;

  const unitPointsConstText = `${__(`loyalty.redeemDialog.unitLabel`)} / ${points} ${__(
    `loyalty.redeemDialog.pointsLabel`
  )}`;

  const pointsAfterRedemption = pointsBalance - points * prizeQuantity;
  const pointsAfterRedemptionLabel = `${__(
    `loyalty.redeemDialog.balanceAfterRedemptionLabel`
  )} ${pointsAfterRedemption} ${__(`loyalty.redeemDialog.pointsLabel`)}`;
  const quantityLimit = Math.floor(pointsBalance / points);

  const isPrizePhysical = rewardType === "physical";
  const isPrizeVirtual = rewardType === "simplified";
  const isPrizeLottery =
    rewardType === "delayed_lottery_physical" ||
    rewardType === "instant_lottery_physical" ||
    rewardType === "delayed_lottery_simplified" ||
    rewardType === "instant_lottery_simplified";

  const isQuantitySelectionVisible = isPrizeVirtual;
  const isAvailableUntilVisible = (isPrizeLottery || isPrizePhysical) && time_to;
  const isEmailVisible = isPrizeVirtual;
  const isAddressRequired = Boolean(isAddressBlockVisible) && (isPrizePhysical || !isLotteryAddressRequirementDisabled);
  const isPrimaryButtonDisabled = isAddressRequired && !userAddress && !isValid;

  return (
    <>
      <h3 className={styles.productName}>{__(prizeName)}</h3>
      <div className={styles.sectionWrapper}>
        <p className={styles.label}>{__("loyalty.redeemDialog.howItWorksLabel")}</p>
        <div
          dangerouslySetInnerHTML={{ __html: sanitize(__(`loyalty.redeemDialog.${rewardType}.howItWorksDescription`)) }}
          className={styles.listWrapper}
        />
      </div>

      <Line />

      {isAvailableUntilVisible && (
        <>
          <div className={styles.sectionWrapper}>
            <p className={styles.label}>{__("loyalty.redeemDialog.availableUntilLabel")}</p>
            <p className={styles.contentText}>{formatDate(time_to)}</p>
          </div>
          <Line />
        </>
      )}

      {isQuantitySelectionVisible && (
        <>
          <div className={classNames(styles.sectionWrapper, styles.sectionWrapperCentered)}>
            <QuantityInput
              value={prizeQuantity}
              onChange={onSetPrizeQuantity}
              min={1}
              max={quantityLimit}
              aria-label={quantityAriaLabel}
              increaseButtonAriaLabel={increaseAriaLabel}
              decreaseButtonAriaLabel={decreaseAriaLabel}
            />
            <p className={styles.contentText}>{unitPointsConstText}</p>
          </div>
          <Line />
        </>
      )}

      <div className={classNames(styles.sectionWrapper, styles.sectionWrapperCentered)}>
        <p className={styles.label}>{__(`loyalty.redeemDialog.costLabel`)}</p>
        <div className={styles.pointsDescriptionWrapper}>
          <p className={styles.pointsNumber}>{calculatedPoints}</p>
          <p className={styles.pointLabelText}>{__(`loyalty.redeemDialog.pointsLabel`)}</p>
        </div>
      </div>

      <Line />

      <div className={styles.sectionWrapper}>
        <p className={styles.label}>{__(`loyalty.redeemDialog.availablePointsLabel`)}</p>
        <p className={styles.contentText}>{pointsBalance}</p>
      </div>

      <p className={styles.contentText}>{pointsAfterRedemptionLabel}</p>
      {isAddressRequired && <AddressDeliveryDetails onEditAddress={onEditAddress} userAddress={userAddress} />}

      {isEmailVisible && (
        <>
          <Line />
          <div className={styles.sectionWrapper}>
            <p className={styles.label}>{__(`loyalty.redeemDialog.emailLabel`)}</p>
            <p className={styles.contentText}>{emailAddress}</p>
          </div>
        </>
      )}

      <ActionButtons
        primaryButtonLabel={__(`loyalty.redeemDialog.${rewardType}.purchaseRewardButtonText`)}
        secondaryButtonLabel={__("loyalty.redeemDialog.cancelButtonText")}
        onPrimaryButtonClick={onConfirm}
        onSecondaryButtonClick={onCloseDialog}
        isPrimaryButtonLoading={isConfirmButtonLoading}
        isPrimaryButtonDisabled={isPrimaryButtonDisabled}
      />

      <div
        className={styles.termsAndConditions}
        dangerouslySetInnerHTML={{
          __html: sanitize(__(`loyalty.redeemDialog.termsAndConditionsText`, [TERMS_PAGE_URL]), {
            ADD_ATTR: ["target"],
          }),
        }}
      />
    </>
  );
};
